import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ForbiddenAlertIcon } from "@images/svgIcons/forbiddenAlertIcon"
import { HighlightingIcon } from "@images/svgIcons/highlightingIcon"
import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { CurrencyFormattedPrice } from "@modules/currency/components/currencyUtils"
import { formatCurrency } from "@modules/currency/currency"
import { StripeAccountErrorItem } from "@modules/finances/components/stripeAccountErrorAlert"
import { StripeErrorProgramCardSettingsButton } from "@modules/finances/components/stripeErrorButtons"
import { getPaymentErrorContent, getPaymentErrorSubtitle } from "@modules/finances/hooks/useStripeAccountErrors"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramSubscriptionActions } from "@modules/programs/components/programSubscriptionActions"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramAthleteSubscription } from "@modules/programs/hooks/useProgramSubscription"
import { ProgramStatus, ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { getFormattedPromoCodeReduction } from "@modules/promoCode/utils/promoCodeUtils"
import { LoadingSpinner } from "@modules/ui/components/loading"

type ProgramSubscriptionCardProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriptionCard({ programId, className, ...props }: ProgramSubscriptionCardProps) {
  const language = useLanguage()
  const { data: program } = useProgram(programId)

  const { programStripeSubscriptionInfos, isLoading: isLoadingSubscription } = useProgramAthleteSubscription(programId)

  const initialPrice = programStripeSubscriptionInfos?.referencePrice || program.price
  const displayedPrice =
    programStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.TRIALING &&
    "nextPayment" in programStripeSubscriptionInfos
      ? programStripeSubscriptionInfos.nextPayment
      : programStripeSubscriptionInfos?.payment &&
          !programStripeSubscriptionInfos.payment.error &&
          !!programStripeSubscriptionInfos.payment.amount
        ? programStripeSubscriptionInfos.payment.amount
        : programStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
            programStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.PAUSE
          ? programStripeSubscriptionInfos.paidPrice
          : initialPrice
  const appliedPromoCode = programStripeSubscriptionInfos?.payment?.promoCode
  const appliedCoupon = programStripeSubscriptionInfos?.payment?.coupon
  const canUserSubscribe =
    program && (("status" in program && program.status !== ProgramStatus.DRAFT) || !("status" in program))
  const userSubscriptionError = programStripeSubscriptionInfos?.payment?.error
  const shouldDisplayTrialInformation =
    "trialDuration" in program &&
    program.trialDuration &&
    canUserSubscribe &&
    !program.subscribed &&
    program.subscriptionStatus !== ProgramSubscriptionStatus.TRIALING &&
    program.isEligibleToTrial
  const shouldDisplayTrialPendingCancellation =
    program.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION
  const shouldDisplayTrialPeriodUsed =
    program.type === ProgramType.VIRTUAL &&
    !program.isEligibleToTrial &&
    program.subscriptionStatus !== ProgramSubscriptionStatus.TRIALING &&
    program.subscriptionStatus !== ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION &&
    program.trialDuration !== null
  const shouldDisplayImpossiblePurchase =
    program.owner?.stripeAccountInError &&
    program.price?.value &&
    program.price.value > 0 &&
    (program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      <Text fontStyle="LS" className="text-textDefault-caption">
        {program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO
          ? word("program.subscription.label").toUpperCase()
          : word("program.price.label").toUpperCase()}
      </Text>

      {/* Subscription error card */}
      {!!userSubscriptionError && (
        <div className="mb-3">
          <StripeAccountErrorItem
            orientation="vertical"
            alertTitle={word("global.stripeError.paymentProblem.title")}
            alertSubtitle={getPaymentErrorSubtitle(userSubscriptionError)}
            alertContent={getPaymentErrorContent(userSubscriptionError)}
            primaryButton={
              <StripeErrorProgramCardSettingsButton programId={programId} paymentError={userSubscriptionError} />
            }
          />
        </div>
      )}

      {/* Trial information */}
      {shouldDisplayTrialInformation && (
        <div
          className="card relative mb-3 flex flex-col items-start gap-2 overflow-hidden"
          style={{ padding: "24px 28px" }}
        >
          <HighlightingIcon className="absolute top-0 left-0 z-0" />
          <div className="z-5 flex items-center gap-2">
            <Text className="z-2 font-bold" fontStyle="TM">
              {word("program.price.trial.tryBeforeSubscribe")}
            </Text>
            <InformationAlertIcon
              color="currentColor"
              backgroundColor="transparent"
              className="z-2 text-textDefault-heading"
            />
          </div>

          {program.trialDuration && program.trialDuration > 0 && (
            <Text className="z-2" fontStyle="BM">
              {`${word("program.price.trial.description")} ${program.trialDuration} ${word("global.day.label", {
                count: program.trialDuration,
              })}`}
            </Text>
          )}
        </div>
      )}

      {/* Trial pending cancellation information */}
      {shouldDisplayTrialPendingCancellation && (
        <div
          className="card relative mb-3 flex flex-col items-start gap-2 overflow-hidden"
          style={{ padding: "24px 28px" }}
        >
          <HighlightingIcon className="absolute top-0 left-0 z-0" />
          <div className="flex items-center gap-2">
            <Text className="z-2" fontStyle="TM">
              {word("program.price.trial.subscriptionCanceled")}
            </Text>
            <InformationAlertIcon
              color="currentColor"
              backgroundColor="transparent"
              className="z-5 text-textDefault-heading"
            />
          </div>
          <Text className="z-2" fontStyle="BM">
            {word("program.price.trial.subscriptionCanceledDescription")}
          </Text>
        </div>
      )}

      {/* Trial period used information */}
      {shouldDisplayTrialPeriodUsed && (
        <div
          className="card relative mb-3 flex flex-col items-start gap-2 overflow-hidden"
          style={{ padding: "24px 28px" }}
        >
          <HighlightingIcon className="absolute top-0 left-0 z-0" />
          <div className="flex items-center gap-2">
            <Text className="z-2" fontStyle="TM">
              {word("program.price.trial.trialPeriodUsedTitle")}
            </Text>
            <InformationAlertIcon
              color="currentColor"
              backgroundColor="transparent"
              className="z-5 text-textDefault-heading"
            />
          </div>
          <Text className="z-2" fontStyle="BM">
            {word("program.price.trial.trialPeriodUsedDescription")}
          </Text>
        </div>
      )}

      {/* Impossible purchase information */}
      {shouldDisplayImpossiblePurchase && (
        <div className="card banner flex items-center gap-4" style={{ padding: "24px 28px" }}>
          <div>
            <ForbiddenAlertIcon
              color="currentColor"
              backgroundColor={"var(--primary-500)"}
              width={50}
              height={50}
              className="text-textDefault-heading"
            />
          </div>

          <div className="flex flex-col">
            <Text className="z-2" fontStyle="TM">
              {word("program.price.impossiblePurchase.title")}
            </Text>
            <Text className="z-2" fontStyle="BM">
              {word("program.price.impossiblePurchase.content")}
            </Text>
          </div>
        </div>
      )}

      {/* Subscription price */}
      <div className="card relative flex flex-col items-start gap-3 overflow-hidden" style={{ padding: "24px 28px" }}>
        {isLoadingSubscription ? (
          <div className="z-2 flex w-full justify-center p-6">
            <LoadingSpinner />
          </div>
        ) : displayedPrice?.value ? (
          <div className="z-2 flex w-full flex-col gap-2">
            {program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO ? (
              <>
                <Text fontStyle="TM">{word("program.price.membership")}</Text>
                <div className="flex items-baseline">
                  <CurrencyFormattedPrice price={{ value: displayedPrice.value, currency: displayedPrice.currency }} />
                  <Text fontStyle="LL" className="ml-2 text-textDefault-caption">
                    {word("program.price.pricePerMonth")}
                  </Text>
                </div>
              </>
            ) : (
              <>
                <Text fontStyle="TM">{word("program.price.singlePurchase")}</Text>
                <div className="flex items-baseline">
                  <CurrencyFormattedPrice price={{ value: displayedPrice.value, currency: displayedPrice.currency }} />
                  <Text fontStyle="LL" className="ml-2 text-textDefault-caption">
                    {word("program.price.onlyOnce")}
                  </Text>
                </div>
              </>
            )}

            {program.subscribed && initialPrice && displayedPrice.value !== initialPrice.value && (
              <div className="flex flex-col gap-1">
                <Text fontStyle="BS">{`${formatCurrency(initialPrice.value, initialPrice.currency, language)} (${word(
                  "program.price.initial",
                )})`}</Text>
                {appliedPromoCode && (
                  <Text fontStyle="BS">{`${getFormattedPromoCodeReduction(appliedPromoCode.type, appliedPromoCode.value, language, initialPrice.currency)} (${word("promoCode.short", { code: appliedPromoCode.code })})`}</Text>
                )}
                {appliedCoupon && (
                  <Text fontStyle="BS">{`- ${getFormattedCouponReduction(appliedCoupon.type, appliedCoupon.value, language, initialPrice.currency)} (${word("program.price.reduction")})`}</Text>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1">
              <Text fontStyle="BM" className="text-textDefault-caption">
                {word("program.price.payingForAll")}
              </Text>
              {program.entranceCheckEnabled && (
                <Text fontStyle="BM" className="text-textDefault-caption">
                  {word("program.price.SASactivated")}
                </Text>
              )}
            </div>
          </div>
        ) : (
          <div className="z-2 flex w-full flex-col gap-2">
            <Text fontStyle="TM" className="mb-1">
              {word("program.price.free")}
            </Text>
            <div className="flex flex-col gap-1">
              <Text fontStyle="BM" className="z-2 text-textDefault-caption">
                {word("program.price.freeForAll")}
              </Text>
              {program.entranceCheckEnabled && (
                <Text fontStyle="BM" className="text-textDefault-caption">
                  {word("program.price.SASactivated")}
                </Text>
              )}
            </div>
          </div>
        )}

        {canUserSubscribe && (
          <div className="z-2 flex w-full">
            <ProgramSubscriptionActions programId={programId} />
          </div>
        )}

        <HustleupSmallIcon
          className="absolute top-0 right-[-40px] h-full bg-contain opacity-50"
          color="var(--surface-background)"
        />
      </div>
    </div>
  )
}
